import { Fragment, useState } from "react";
import { RotateCcw, Trash2, AlertCircle, ChevronDown, Ruler } from "lucide-react";
import { ARCH_STYLES, badConfig, badRating, CONFIG_ERR, glassSummary, gridSummary, isSliding, money, sketchParams, SPEC_FIELDS } from "@/lib/pricing";
import { LineSketch } from "@/components/quote/LineSketch";
import { CatalogPicker } from "@/components/quote/CatalogPicker";
import { Photo, ProductPicker } from "@/components/quote/ProductPicker";
import { DesignButton } from "@/components/designer/DesignButton";
import { DesignCanvas } from "@/components/designer/DesignCanvas";
import { SeriesField } from "@/components/quote/SeriesField";
import { ComponentFields } from "@/components/quote/ComponentFields";
import { ArchFields, GlassFields, GridFields } from "@/components/quote/GlassFields";
import { useAuth } from "@/context/AuthContext";
import { isStaff } from "@/lib/staffView";
import { LinePhoto, MatchChip } from "@/components/quote/MatchChip";
import { DesignSpecs } from "@/components/designer/DesignSpecs";
import { useLines } from "@/lib/components";

const num = (v) => (v === null || v === undefined ? "" : v);

function TypeSelect({ item, types, onChange, idx, warn }) {
  const known = types.some((t) => t.id === item.type_id);
  return (
    <select data-testid={`quote-item-type-select-${idx}`} value={item.type_id && (known || item.type_name !== "Unmapped") ? item.type_id : ""}
      onChange={(e) => onChange(e.target.value)}
      className={`cell-input cursor-pointer pr-1 ${warn ? "cell-warn text-red-300" : ""}`}>
      <option value="" className="bg-slate-900">Unmapped</option>
      {!known && item.type_id && item.type_name !== "Unmapped" && (
        <option value={item.type_id} className="bg-slate-900">{item.type_name} (saved)</option>
      )}
      {["window", "door"].map((cat) => (
        <optgroup key={cat} label={cat === "window" ? "Windows" : "Doors"} className="bg-slate-900">
          {types.filter((t) => t.category === cat).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function SpecField({ label, testId, value, onChange, placeholder, wide }) {
  return (
    <label className={`block ${wide ? "col-span-2" : ""}`}>
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <input data-testid={testId} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full rounded-md border border-slate-800 bg-slate-900/70 px-2 text-sm text-slate-100 outline-none transition-colors focus:border-amber-500/70" />
    </label>
  );
}

function SpecsRow({ row, idx, onPatch, roPlaceholder, options, types }) {
  const { locked } = useLines();
  return (
    <tr data-testid={`quote-item-specs-row-${idx}`} className="qi-specs border-b border-slate-800/70 bg-slate-950/40">
      <td colSpan={14} className="px-6 py-4">
        <div className="flex flex-col gap-6 lg:flex-row">
          {row.catalog_photo && <LinePhoto k={row.catalog_photo} idx={idx} className="h-[240px] w-[180px] shrink-0" />}
          {row.design?.root ? <DesignCanvas design={row.design} interactive={false} className="h-[240px] w-[260px] shrink-0" testId={`quote-item-design-drawing-${idx}`} /> : (
          <LineSketch params={sketchParams(row, row.calc.mapped)} boxW={220} boxH={240} className="h-[240px] w-[220px] shrink-0 p-1"
            testId={`quote-item-sketch-large-${idx}`} />)}
          <div className="min-w-0 flex-1 space-y-4">
            <MatchChip row={row} idx={idx} locked={locked} onPatch={onPatch} types={types} />
            {row.design?.root && <DesignSpecs row={row} idx={idx} />}
            <ArchFields row={row} idx={idx} onChange={onPatch} />
            <SeriesField row={row} idx={idx} onPatch={onPatch} />
            <GridFields row={row} onChange={onPatch} sfx={`-${idx}`} />
            <GlassFields row={row} idx={idx} options={options} onChange={onPatch} />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Category</span>
            <select data-testid={`quote-item-category-select-${idx}`} value={row.category} onChange={(e) => onPatch({ category: e.target.value })}
              className="h-8 w-full rounded-md border border-slate-800 bg-slate-900/70 px-2 text-sm text-slate-100">
              <option value="window">Window</option>
              <option value="door">Door</option>
            </select>
          </label>
          {SPEC_FIELDS.map(([k, label]) => (
            <SpecField key={k} label={label} testId={`quote-item-${k.replace(/_/g, "-")}-input-${idx}`} value={row[k]}
              onChange={(v) => onPatch({ [k]: v })} wide={k === "glass_details" || k === "product_name"}
              placeholder={k === "product_name" ? row.type_name !== "Unmapped" ? row.type_name : row.raw_type : ""} />
          ))}
          {row.category === "door" && (
            <SpecField label="SKU" testId={`quote-item-sku-input-${idx}`} value={row.sku} onChange={(v) => onPatch({ sku: v })} />
          )}
          <SpecField label="Rough opening" testId={`quote-item-rough-opening-input-${idx}`} value={row.rough_opening}
            placeholder={roPlaceholder} onChange={(v) => onPatch({ rough_opening: v })} />
          <SpecField label="Notes" testId={`quote-item-notes-input-${idx}`} value={row.notes} wide onChange={(v) => onPatch({ notes: v })} />
        </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

const fmtIn = (v) => `${Number(v.toFixed(2))}"`;

export function ItemRow({ row, idx, types, onPatch, onType, onDelete, company, doorItems = [], onPick }) {
  const { locked: linesLocked } = useLines();
  const staff = isStaff(useAuth().user);
  const itemType = types.find((t) => t.id === row.type_id);
  const showPicker = row.pricing_mode === "item" || (itemType?.category === "door" && itemType?.pricing_mode === "item");
  const allowance = company?.rough_opening_allowance;
  const [open, setOpen] = useState(false);
  const { calc } = row;
  const lowConf = row.confidence !== null && row.confidence !== undefined && row.confidence < 0.6;
  const priceValue = calc.edited ? num(row.price_override) : calc.suggested.toFixed(2);
  const w = Number(row.width_in), h = Number(row.height_in), a = Number(allowance) || 0;
  const roPlaceholder = calc.sizeOk ? `${fmtIn(w + a)}x${fmtIn(h + a)}` : "";
  const input = (field, props = {}) => (
    <input data-testid={`quote-item-${field.replace("_in", "")}-input-${idx}`} value={num(row[field])}
      onChange={(e) => onPatch({ [field]: e.target.value })} {...props}
      className={`cell-input ${props.className || ""}`} />
  );
  return (
    <Fragment>
      <tr data-testid={`quote-item-row-${idx}`} className={`qi-row group border-b border-slate-800/70 transition-colors hover:bg-slate-900/40 ${lowConf ? "bg-amber-500/[0.03]" : ""}`}>
        <td className="qi-no px-3 py-1.5 font-mono-num text-xs text-slate-500" data-testid={`quote-item-line-no-${idx}`}>{String(idx + 1).padStart(2, "0")}</td>
        <td className="qi-draw px-1 py-1.5">
          <button onClick={() => setOpen((o) => !o)} title="Open specs" className="block">
            {row.design?.root ? <DesignCanvas design={row.design} interactive={false} className="h-[60px] w-[54px]" testId={`quote-item-sketch-${idx}`} /> : (
            <LineSketch params={sketchParams(row, calc.mapped)} boxW={110} boxH={120} className="h-[60px] w-[54px] p-0.5"
              testId={`quote-item-sketch-${idx}`} />)}
          </button>
        </td>
        <td data-label="Mark" className="px-1 py-1.5">
          <div className="flex items-center gap-1">
            {lowConf && <AlertCircle data-testid={`low-confidence-icon-${idx}`} className="h-3.5 w-3.5 shrink-0 text-amber-400" />}
            {input("mark", { placeholder: "W1", title: row.mark, className: "min-w-[80px] font-mono-num text-amber-200" })}
          </div>
        </td>
        <td data-label="Location" className="qi-loc px-1 py-1.5">{input("location", { placeholder: "Room", className: "text-slate-300" })}</td>
        <td data-label="Type" className="qi-type px-1 py-1.5">
          <div className="qi-type-inner w-[200px]">
          <TypeSelect item={row} types={types} idx={idx} warn={!calc.mapped} onChange={onType} />
          <ProductPicker row={row} idx={idx} onPatch={onPatch} />
          {!linesLocked && <DesignButton row={row} idx={idx} onPatch={onPatch} owner={!staff} types={types} />}
          {row.raw_type && row.raw_type !== row.type_name && (
            <p className="truncate px-2 text-[11px] text-slate-500" title={row.raw_type}>read as "{row.raw_type}"</p>
          )}
          {(glassSummary(row) || row.arch || row.grids || row.shape_name) && (
            <p data-testid={`quote-item-glass-summary-${idx}`} className="truncate px-2 text-[11px] text-sky-300/80" title={glassSummary(row)}>
              {[row.shape_name ? `Shape: ${row.shape_name}` : row.arch ? `Arch: ${(ARCH_STYLES.find(([v]) => v === row.arch_style) || [0, "Half Round"])[1]}` : "", glassSummary(row),
                row.grids ? `Grids: ${gridSummary(row)}` : ""].filter(Boolean).join(" · ")}
            </p>
          )}
          {showPicker && <CatalogPicker row={row} items={doorItems} idx={idx} onPick={onPick} />}
          {row.design?.root ? <p data-testid={`quote-item-design-summary-${idx}`} className="text-xs text-slate-400">{row.design_summary}</p>
            : <ComponentFields key={row.product_line_id || "none"} row={row} idx={idx} onPatch={onPatch} staff={staff} />}
          {isSliding(row) && (
            <div className="mx-2 mt-1">
              <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Config
                <input data-testid={`quote-item-config-input-${idx}`} value={row.config || ""} maxLength={12} placeholder="XO"
                  title="X = sliding, O = fixed, viewed from the exterior" onChange={(e) => onPatch({ config: e.target.value.toUpperCase() })}
                  className={`h-6 w-20 rounded border bg-slate-900/70 px-1.5 font-mono-num text-xs tracking-widest text-slate-100 outline-none ${badConfig(row.config) ? "border-red-500" : "border-slate-800 focus:border-amber-500/70"}`} />
              </label>
              {badConfig(row.config) && <p data-testid={`quote-item-config-error-${idx}`} className="mt-0.5 text-[10px] text-red-400">{CONFIG_ERR}</p>}
            </div>
          )}
          {row.nominal && (
            <p data-testid={`nominal-badge-${idx}`} title="Size read from a plan callout, not a schedule"
              className="mx-2 mt-0.5 inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold text-sky-200">
              <Ruler className="h-3 w-3" /> Nominal, please verify
            </p>
          )}
          {(badRating(row.u_value) || badRating(row.shgc)) && (
            <p data-testid={`quote-item-rating-warning-${idx}`} className="px-2 text-[11px] text-red-400">Check U-factor / SHGC</p>
          )}
          {row.review_flag === "possible_duplicate" && (
            <button data-testid={`possible-duplicate-badge-${idx}`} title="Click to mark as reviewed" onClick={() => onPatch({ review_flag: "" })}
              className="mx-2 mt-0.5 inline-flex items-center gap-1 rounded-full border border-orange-500/50 bg-orange-500/10 px-2 py-0.5 text-[10px] font-semibold text-orange-300 transition-colors hover:bg-orange-500/20">
              Possible duplicate. Review ✓
            </button>
          )}
          {row.source_file && (
            <p data-testid={`quote-item-source-${idx}`} className="truncate px-2 text-[10px] text-slate-500" title={row.source_file}>From {row.source_file}</p>
          )}
          </div>
        </td>
        <td data-label="W (in)" className="px-1 py-1.5">{input("width_in", { type: "number", min: 0, step: "any", placeholder: "—", className: `min-w-[72px] px-1 font-mono-num text-right ${calc.missingW ? "cell-warn" : ""}` })}</td>
        <td data-label="H (in)" className="px-1 py-1.5">{input("height_in", { type: "number", min: 0, step: "any", placeholder: "—", className: `min-w-[72px] px-1 font-mono-num text-right ${calc.missingH ? "cell-warn" : ""}` })}</td>
        <td data-label="Qty" className="px-1 py-1.5">{input("qty", { type: "number", min: 0, step: 1, className: "font-mono-num text-right" })}</td>
        <td data-label="Sq ft" data-testid={`quote-item-sqft-${idx}`} className="px-2 py-1.5 text-right font-mono-num text-sm text-slate-400">{calc.sqft.toFixed(2)}</td>
        {!staff && <td data-label="Cost" data-testid={`quote-item-cost-${idx}`} className="px-2 py-1.5 text-right font-mono-num text-sm text-slate-400">{money(calc.cost)}</td>}
        <td data-label="Suggested" data-testid={`quote-item-suggested-${idx}`} className="px-2 py-1.5 text-right font-mono-num text-sm text-slate-300">{money(calc.suggested)}</td>
        <td data-label="Item price" data-testid={`quote-item-unit-price-${idx}`} className="px-2 py-1.5 text-right font-mono-num text-sm text-slate-300">{money(calc.unit)}</td>
        <td data-label="Line total" className="qi-total min-w-[150px] px-1 py-1.5">
          <div className="flex items-center gap-1">
            <div className="relative w-full min-w-[110px]">
              <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
              <input data-testid={`quote-item-final-price-input-${idx}`} type="number" min={0} step="0.01" value={priceValue}
                onChange={(e) => onPatch({ price_override: e.target.value === "" ? null : e.target.value, price_source: "" })}
                className={`cell-input pl-5 text-right font-mono-num font-semibold ${calc.edited ? "border-amber-500/50 text-amber-300" : "text-slate-100"}`} />
            </div>
            {calc.edited && (
              <button data-testid={`reset-price-button-${idx}`} title="Reset to suggested" onClick={() => onPatch({ price_override: null, price_source: "", check_price: false })}
                className="rounded p-1 text-amber-400 transition-colors hover:bg-amber-500/10"><RotateCcw className="h-3.5 w-3.5" /></button>
            )}
          </div>
          {calc.edited && !["unmatched", "designer"].includes(row.price_source) && (row.price_source === "estimate"
            ? <span data-testid={`from-estimate-badge-${idx}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-sky-400">From estimate</span>
            : <span data-testid={`edited-price-badge-${idx}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-amber-400">Edited</span>)}
          {row.check_price && <span data-testid={`check-price-badge-${idx}`} className="ml-2 rounded bg-amber-500 px-1 text-[10px] font-bold uppercase text-white">Check price</span>}
          {!staff && row.price_source === "estimate" && calc.edited && (
            <span data-testid={`estimate-margin-${idx}`} className="ml-2 block text-[10px] text-slate-500">Margin {money(calc.final - calc.cost)}</span>
          )}
        </td>
        <td className="qi-actions px-1 py-1.5 text-right">
          <div className="flex justify-end">
            <button data-testid={`toggle-specs-button-${idx}`} onClick={() => setOpen((o) => !o)} title="Specs"
              className={`rounded-md p-1.5 transition-colors hover:bg-slate-800 ${open ? "text-amber-300" : "text-slate-500"}`}>
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <button data-testid={`delete-item-button-${idx}`} onClick={onDelete} title="Delete row"
              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        </td>
      </tr>
      {open && <SpecsRow row={row} idx={idx} onPatch={onPatch} roPlaceholder={roPlaceholder} options={company?.glass_options} types={types} />}
    </Fragment>
  );
}
